import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SafeUrl } from '@angular/platform-browser';
import { orderBy } from 'lodash-es';
import { BehaviorSubject, catchError, combineLatest, map, Observable, of } from 'rxjs';
import { User } from '@microsoft/microsoft-graph-types';
import { HttpStatus } from '@nimbus-assistant/enums/http-status';
import { IAssistantUserAutocompleteOption } from '@nimbus-assistant/interfaces/assistant-user-autocomplete';
import { UserSearchMapper } from '@nimbus-library/features/shared/services/user-search/mappers/user-search.mapper';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ContactSearchMapper } from '@nimbus-library/features/shared/services/user-search/mappers/contact-search.mapper';
import { SearchParserResult } from '@nimbus-library/features/graph/models/search-parser-result.model';
import { LoggingService } from './logging.service';
import { GraphApiService } from './graph-api.service';
import { ErrorService } from './error.service';
import { AuthService } from './auth.service';
import { ContactsApiService } from './api/contacts-api.service';
import { ImageService } from './image.service';
import { ServicesService } from './services.service';
import { ApiRoutes } from './api/api-routes.consts';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class ContactsService {
  public constructor(
    private readonly graphApiService: GraphApiService,
    private readonly errorService: ErrorService,
    private readonly authService: AuthService,
    private readonly loggingService: LoggingService,
    private readonly contactsApiService: ContactsApiService,
    private readonly imageService: ImageService,
    private readonly teamService: ServicesService,
  ) {}

  public search(input: string, tenantId: string, includeServices: boolean = true): Observable<IAssistantUserAutocompleteOption[]> {
    const searchInput = new SearchParserResult([]);
    searchInput.plainSearchText = input;
    searchInput.originalInput = input;

    return combineLatest([this.graphContactsSearch(searchInput, tenantId, includeServices), this.nimbusContactsSearch(input)]).pipe(
      map((data) => [...data[0], ...data[1]]),
      map((data: IAssistantUserAutocompleteOption[]) =>
        data
          .map((item) => {
            const isInServiceList = !!(this.teamService.serviceApplicationMappings$?.value || []).find((service) =>
              item.callOptions.find((option) => option.text === service.upn),
            );
            const isService = item.isService || isInServiceList;
            return {
              ...item,
              isService,
              // eslint-disable-next-line arrow-body-style
              callOptions: item.callOptions.map((x) => {
                return { ...x, isService, name: item.name };
              }),
            };
          })
          .filter((item) => !item.isService || includeServices),
      ),
      map((data) => orderBy(data, (d) => d.name.toLowerCase())),
    );
  }

  private nimbusContactsSearch(searchInput: string): Observable<IAssistantUserAutocompleteOption[]> {
    return this.contactsApiService.searchNimbusContacts(searchInput).pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map((contacts: any[]) =>
        contacts.map((contact) => {
          contact.displayName = contact.displayName
            ? contact.displayName
            : contact.firstName || contact.lastName
              ? contact.firstName
                ? contact.lastName
                  ? `${contact.firstName} ${contact.lastName}`
                  : contact.firstName
                : contact.lastName
              : contact.userPrincipalName || contact.externalId;

          const userOption: IAssistantUserAutocompleteOption = new ContactSearchMapper().handle(
            contact,
          ) as IAssistantUserAutocompleteOption;
          userOption.isService = contact.isService;

          const requestUrl = ApiRoutes.Contacts.addressBookIcon(contact.addressBookId);
          return this.setImageSafeUrl(userOption, requestUrl);
        }),
      ),
      catchError((err) => {
        this.loggingService.Error(JSON.stringify(err));
        return of([]);
      }),
    );
  }

  private graphContactsSearch(
    searchInput: SearchParserResult,
    tenantId: string,
    includeServices: boolean,
  ): Observable<IAssistantUserAutocompleteOption[]> {
    return this.graphApiService.getContacts(searchInput, includeServices).pipe(
      map((x: { value: User[] }) =>
        x.value.map((user: User) => {
          const userOption = new UserSearchMapper().handle(user, tenantId) as IAssistantUserAutocompleteOption;
          const requestUrl = ApiRoutes.Contacts.graphUserIcon(user.id);
          return this.setImageSafeUrl(userOption, requestUrl);
        }),
      ),
      catchError((err) => {
        if (err.status === HttpStatus.FORBIDDEN_403) {
          this.errorService.showError(
            TranslationKeys.WarningNoGraphPermissions,
            TranslationKeys.WarningContactYourAdministratorWithLink,
            this.authService.getPermissionLink(),
          );
        }
        return of([]);
      }),
    );
  }

  private setImageSafeUrl(userOption: IAssistantUserAutocompleteOption, requestUrl: string): IAssistantUserAutocompleteOption {
    userOption.imageSafeUrl$ = new BehaviorSubject<SafeUrl>(null);
    userOption.image = null;
    this.getIconSafeUrl(requestUrl)
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        userOption.imageSafeUrl$.next(result);
      });
    return userOption;
  }

  private getIconSafeUrl(imageSrc: string): Observable<SafeUrl> {
    if (!imageSrc) {
      return of(null);
    }
    return this.contactsApiService.getNimbusContactIcon(imageSrc).pipe(
      map((icon) => this.imageService.getImageBlobUrl(icon)),
      catchError((err) => {
        this.loggingService.Error(JSON.stringify(err));
        return of(null);
      }),
    );
  }
}
